
import React, { useState, useMemo } from 'react';
import { SEASONS } from './constants';
import { Contestant, Season } from './types';
import ContestantCard from './components/ContestantCard';
import VideoModal from './components/VideoModal';

const App: React.FC = () => {
  const [selectedSeason, setSelectedSeason] = useState<number>(SEASONS[0].number);
  const [selectedContestant, setSelectedContestant] = useState<Contestant | null>(null);

  const currentSeason = useMemo(() => 
    SEASONS.find(s => s.number === selectedSeason) || SEASONS[0]
  , [selectedSeason]);

  return (
    <div className="min-h-screen bg-slate-950 text-white selection:bg-pink-500 selection:text-white">
      {/* Header / Hero */}
      <header className="relative py-16 md:py-24 px-4 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-pink-600 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600 rounded-full blur-[100px] animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-pink-500 animate-ping"></span>
            <span className="text-xs font-semibold uppercase tracking-widest text-pink-400">The Ultimate Queen Hub</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-playfair font-bold mb-6 tracking-tight">
            Splitsvilla <span className="gradient-text">Divas</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Step into the villa of glam and drama. Stream the exclusive introduction trailers 
            of your favorite girls across all seasons.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 pb-20">
        
        {/* Season Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 sticky top-4 z-40">
          <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 p-2 rounded-2xl flex items-center gap-2 shadow-2xl">
            {SEASONS.map((season) => (
              <button
                key={season.number}
                onClick={() => setSelectedSeason(season.number)}
                className={`
                  px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300
                  ${selectedSeason === season.number 
                    ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg shadow-pink-600/20' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'}
                `}
              >
                S{season.number}
              </button>
            ))}
          </div>
        </div>

        {/* Season Header */}
        <div className="mb-12 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/5 pb-8">
          <div>
            <h2 className="text-3xl font-playfair font-bold text-white flex items-center gap-3 justify-center md:justify-start">
              Season {currentSeason.number}: {currentSeason.theme}
            </h2>
            <p className="text-pink-500 font-medium">{currentSeason.subtitle}</p>
          </div>
          <p className="text-slate-500 text-sm font-medium">
            Featuring {currentSeason.contestants.length} Introduction Trailers
          </p>
        </div>

        {/* Contestant Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {currentSeason.contestants.map((girl) => (
            <ContestantCard 
              key={girl.id} 
              contestant={girl} 
              onSelect={setSelectedContestant}
            />
          ))}
        </div>

        {/* Empty State */}
        {currentSeason.contestants.length === 0 && (
          <div className="py-20 text-center">
            <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-700">
               <i className="fas fa-video-slash text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-400">No clips found for this season</h3>
            <p className="text-slate-600">We're updating the archives. Check back soon!</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <h2 className="text-2xl font-playfair font-bold gradient-text">Splitsvilla Diva Stream</h2>
            <p className="text-slate-500 text-sm mt-1">Made for the ultimate fans of reality TV.</p>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="text-slate-400 hover:text-pink-500 transition-colors"><i className="fab fa-instagram text-xl"></i></a>
            <a href="#" className="text-slate-400 hover:text-pink-500 transition-colors"><i className="fab fa-twitter text-xl"></i></a>
            <a href="#" className="text-slate-400 hover:text-pink-500 transition-colors"><i className="fab fa-youtube text-xl"></i></a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-white/5 text-center">
           <p className="text-slate-600 text-xs">© 2024 Splitsvilla Diva Stream. All videos are property of MTV India and Viacom18.</p>
        </div>
      </footer>

      {/* Video Overlay */}
      <VideoModal 
        contestant={selectedContestant} 
        onClose={() => setSelectedContestant(null)} 
      />
    </div>
  );
};

export default App;
